import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';

import { cssColor } from '@/theme/colors';
import { DEFAULT_STEP, LAST_STEP, planForStep } from '../model/pricing';

const ACCENT = cssColor('bg.primary');
const TRACK = cssColor('bg.strong');
const THUMB = cssColor('bg.default');
const FOCUS_RING = 'focus-visible:focus-ring';

/** Il calcolatore dei prezzi della landing: uno slider sui veicoli e il piano che ne esce. */
export function PricingSlider() {
  const [step, setStep] = useState(DEFAULT_STEP);
  const plan = planForStep(step);
  const progress = (step / LAST_STEP) * 100;

  return (
    <section className="mx-auto max-w-3xl p-6">
      <div className="mx-auto mb-10 max-w-2xl text-center">
        <span className="rounded-full border border-default bg-muted px-3 py-1 text-eyebrow text-link">
          Prezzi
        </span>
        <h2 className="mt-6 text-balance text-4xl font-semibold tracking-tight text-strong md:text-5xl">
          Un prezzo per veicolo, che scende con la flotta
        </h2>
        <p className="mt-4 text-balance text-lg text-subtle">
          Da €16 a €8 per veicolo al mese: più mezzi gestisci, meno paghi per ciascuno.
        </p>
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        <div className="relative flex-1 rounded-xl border border-default bg-default/70 p-8 pb-24">
          <h3 className="mb-4 text-card-title text-strong">Calcola il tuo prezzo</h3>
          <p className="mb-8 text-3xl font-bold tabular-nums text-strong">{plan.label}</p>
          <input
            type="range"
            min={0}
            max={LAST_STEP}
            step={1}
            value={step}
            onChange={(e) => setStep(Number(e.target.value))}
            aria-label="Numero di veicoli"
            aria-valuetext={plan.label}
            className={`pricing-slider mb-4 h-3 w-full appearance-none rounded-sm ${FOCUS_RING}`}
            style={{
              background: `linear-gradient(to right, ${ACCENT} 0%, ${ACCENT} ${progress}%, ${TRACK} ${progress}%, ${TRACK} 100%)`,
            }}
          />
          <div className="mb-2 flex justify-between text-caption tabular-nums text-subtle">
            <span>1</span>
            <span>1.000+</span>
          </div>

          <style>{`
            .pricing-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 28px;
              height: 28px;
              background: ${THUMB};
              border: 2px solid ${ACCENT};
              border-radius: 50%;
              cursor: pointer;
              margin-top: -1px;
              box-shadow: var(--shadow-raised);
              position: relative;
            }
            .pricing-slider::-moz-range-thumb {
              width: 26px;
              height: 26px;
              background: ${THUMB};
              border: 2px solid ${ACCENT};
              border-radius: 50%;
              cursor: pointer;
              box-shadow: var(--shadow-raised);
            }
          `}</style>

          <div className="absolute bottom-8 left-8 right-8 flex items-center justify-between text-sm text-subtle">
            <span>Serve una soluzione su misura?</span>
            <Link
              to="/richiedi-demo"
              className={`flex items-center gap-1 rounded-sm font-medium text-link hover:underline ${FOCUS_RING}`}
            >
              Contattaci
              <ArrowRight aria-hidden className="size-3.5" />
            </Link>
          </div>
        </div>

        <div className="flex-1 rounded-xl border border-default bg-muted/40 p-8">
          <h3 className="mb-4 text-card-title text-strong">Il tuo piano</h3>
          <div className="mb-2">
            <p className="mb-1 text-4xl font-bold tabular-nums text-strong">
              {plan.custom ? 'Su misura' : plan.total}
            </p>
            {!plan.custom && (
              <p className="text-sm font-medium tabular-nums text-brand">{plan.breakdown}</p>
            )}
          </div>
          <p className="mt-8 text-sm leading-relaxed text-subtle">
            {plan.custom
              ? "Per flotte oltre i 1.000 veicoli costruiamo un'offerta dedicata, con onboarding e supporto su misura. Parliamone."
              : 'Tutti i moduli inclusi: tracking, manutenzioni, carburante, multe, documenti e dashboard. Nessun costo nascosto, disdici quando vuoi.'}
          </p>
          <Link
            to="/richiedi-demo"
            className={`mt-8 inline-block rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-on-emphasis transition-colors duration-150 hover:bg-primary-hover ${FOCUS_RING}`}
          >
            {plan.custom ? 'Contattaci' : 'Richiedi demo'}
          </Link>
        </div>
      </div>
    </section>
  );
}
